"use client";

import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/ux/reveal";
import { media } from "@/lib/media";

/* Depoimentos em vídeo (reais, do site atual — re-encodados p/ H.264).
   Hover: preview sem som. Clique: play com som, do início, com barra de
   progresso; os demais pausam. preload="none" + poster = página leve. */

const DEPOIMENTOS = [
  {
    id: "duda",
    quote: "Checkout extremamente rápido e suporte impecável.",
    nome: "Duda Fontanezi",
    cargo: "CEO da Brogan",
  },
  {
    id: "leo_b2b",
    quote: "A plataforma que fez nosso e-commerce crescer com velocidade e segurança.",
    nome: "Leonardo",
    cargo: "CEO da Kallucci · B2B",
  },
  {
    id: "sandro",
    quote: "Parceria estratégica que impulsionou nosso crescimento e melhorou a experiência do cliente.",
    nome: "Sandro",
    cargo: "CEO da Botineiros",
  },
  {
    id: "leo",
    quote: "Tecnologia de ponta e personalização avançada para o nosso negócio.",
    nome: "Leonardo",
    cargo: "CEO da Kallucci",
  },
];

export default function Depoimentos() {
  const videos = useRef<(HTMLVideoElement | null)[]>([]);
  const [ativo, setAtivo] = useState<number | null>(null);
  const [pct, setPct] = useState(0);
  const secao = useRef<HTMLElement>(null);
  const [capas, setCapas] = useState(false);
  useEffect(() => {
    const el = secao.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && (setCapas(true), io.disconnect()), { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const tocar = (i: number) => {
    const v = videos.current[i];
    if (!v) return;
    if (ativo === i) {
      v.pause();
      setAtivo(null);
      return;
    }
    videos.current.forEach((o, d) => {
      if (o && d !== i) {
        o.pause();
        o.muted = true;
      }
    });
    setPct(0);
    v.muted = false;
    v.currentTime = 0;
    v.play().catch(() => {});
    setAtivo(i);
  };

  const preview = (i: number, entrar: boolean) => {
    const v = videos.current[i];
    if (!v || ativo === i) return;
    if (entrar) {
      v.muted = true;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  return (
    <section ref={secao} id="cases" className="scroll-mt-20 bg-mist">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="eyebrow text-smoke">(Quem cresce com a Shoppub)</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl text-[clamp(2.1rem,4.6vw,3.8rem)] font-bold leading-[1.02]">
              Resultados reais, na voz de <span className="mark-lime">quem vende</span>
            </h2>
            <p className="max-w-sm text-lg leading-relaxed text-smoke">
              Mais de 500 marcas premium vendem todos os dias na plataforma. Dá o play e
              ouve direto delas.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden py-3 lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:pb-0">
          {DEPOIMENTOS.map((d, i) => (
            <Reveal key={d.id} delay={Math.min(i * 0.07, 0.25)} className="w-[76%] shrink-0 snap-center max-lg:opacity-100! max-lg:[transform:none]! sm:w-[46%] lg:w-auto">
              <button
                type="button"
                onClick={() => tocar(i)}
                onMouseEnter={() => preview(i, true)}
                onMouseLeave={() => preview(i, false)}
                className={`group relative block w-full cursor-pointer overflow-hidden rounded-card text-left transition-all duration-500 ${
                  ativo === i
                    ? "ring-2 ring-lime shadow-[0_30px_70px_-30px_rgba(201,241,88,0.35)] lg:rotate-0 lg:scale-[1.02]"
                    : `shadow-[0_24px_55px_-35px_rgba(10,10,11,0.5)] hover:lg:rotate-0 ${
                        i % 2 === 0 ? "lg:rotate-[-1.2deg]" : "lg:rotate-[1.2deg]"
                      }`
                }`}
              >
                <video
                  ref={(el) => {
                    videos.current[i] = el;
                  }}
                  src={media(`/media/depoimentos/${d.id}.mp4?v=2`)}
                  poster={capas ? `/media/depoimentos/${d.id}.webp` : undefined}
                  preload="none"
                  playsInline
                  muted
                  onTimeUpdate={(e) => {
                    if (ativo === i) {
                      const v = e.currentTarget;
                      setPct((v.currentTime / v.duration) * 100);
                    }
                  }}
                  onEnded={() => setAtivo(null)}
                  className="aspect-[9/16] w-full object-cover"
                />

                {/* véu para legibilidade */}
                <div
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-t from-ink from-35% via-ink/75 via-55% to-ink/20 transition-opacity duration-500 ${
                    ativo === i ? "opacity-60" : "opacity-100"
                  }`}
                />

                {/* controle de play/som */}
                <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-paper/90 text-ink backdrop-blur transition group-hover:bg-lime">
                  {ativo === i ? (
                    <span className="flex items-end gap-[2.5px]" aria-hidden>
                      <span className="w-[3px] animate-[eq_0.8s_ease-in-out_infinite] bg-ink" style={{ height: 10 }} />
                      <span className="w-[3px] animate-[eq_0.8s_ease-in-out_0.25s_infinite] bg-ink" style={{ height: 15 }} />
                      <span className="w-[3px] animate-[eq_0.8s_ease-in-out_0.5s_infinite] bg-ink" style={{ height: 8 }} />
                    </span>
                  ) : (
                    <svg viewBox="0 0 24 24" className="ml-0.5 h-4 w-4" fill="currentColor" aria-hidden>
                      <path d="M7 5l12 7-12 7z" />
                    </svg>
                  )}
                </span>

                <span className="sr-only">{ativo === i ? "Pausar" : "Assistir"} depoimento: </span>

                {/* quote + autor */}
                <span
                  className={`absolute inset-x-0 bottom-0 p-5 transition-all duration-500 ${
                    ativo === i ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
                  }`}
                >
                  <span className="block font-display text-[17px] font-bold leading-snug text-paper">
                    “{d.quote}”
                  </span>
                  <span className="mt-3 block text-[13px] font-semibold text-paper">{d.nome}</span>
                  <span className="block text-[12px] text-paper/60">{d.cargo}</span>
                </span>

                {/* autor compacto durante o play */}
                <span
                  className={`absolute inset-x-0 bottom-0 flex items-center justify-between p-4 transition-all duration-500 ${
                    ativo === i ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
                  }`}
                >
                  <span>
                    <span className="block text-[13px] font-bold text-paper">{d.nome}</span>
                    <span className="block text-[11px] text-paper/70">{d.cargo}</span>
                  </span>
                </span>

                {/* progresso */}
                {ativo === i && (
                  <span className="absolute inset-x-0 bottom-0 h-1 bg-white/15">
                    <span className="block h-full bg-lime" style={{ width: `${pct}%` }} />
                  </span>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
